import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { node } from "prop-types";
import { APP_TABLE } from "./constant";
import useCoreStore from "../../store/coreStore";
import useProjectStore from "../../store/projectStore";
import { useEnsureHosts, useEnsureApplications, useEnsureAssessmentsSummary } from "../../store/useCoreData";
import { filterApp, normalizeApp } from "../../components/app-rewrite/apps/helper";
import { getAppToLinkedRepositoriesMap } from "./helper";

const AppContext = React.createContext({
    appTable: APP_TABLE.APPS,
    assessments: [],
    assessmentsById: {},
    clientAssessment: null,
    setAppTable: () => {}
});

const propTypes = {
    children: node.isRequired
};

function AppContextProvider (props) {
    const currentProjectId = useProjectStore(s => s.currentProjectId);
    const { children, clientAssessment } = props;
    const [appTable, setAppTable] = useState(APP_TABLE.APPS);
    const apps = useCoreStore(s => s.applications);
    const hosts = useCoreStore(s => s.hosts);
    const rawAssessmentsSummary = useCoreStore(s => s.assessmentsSummary);

    // Derive version map locally — shape expected by normalizeApp: { [machineId]: { name, scanTime, machineId } }
    const version = useMemo(() => hosts.reduce((acc, h) => {
        acc[h.machineId] = { name: h.name, scanTime: h.timestamp, machineId: h.machineId };
        return acc;
    }, {}), [hosts]);

    const [appInstances, setAppInstances] = useState([]);
    const [appComponents, setAppComponents] = useState([]);
    const [appLinkedRepositoriesMap, setAppLinkedRepositoriesMap] = useState([]);
    const [enabled, setEnabled] = useState(false);

    const { id } = useParams();
    const isSeparateRouteAssessmentsDetails = useMemo(() => Boolean(id), [id]);

    useEnsureHosts();
    useEnsureApplications();
    useEnsureAssessmentsSummary();

    const assessmentsById = useMemo(() =>
        rawAssessmentsSummary.reduce((acc, assessmentSummary) => {
            acc[assessmentSummary.applicationId] = assessmentSummary;
            return acc;
        }, {}),
    [rawAssessmentsSummary]);

    useEffect(() => {
        if (version && apps.length > 0) {
            const { appInstances, appComponents } = filterApp(normalizeApp(apps, version));

            setAppInstances(appInstances);
            setAppComponents(appComponents);
            setAppLinkedRepositoriesMap(getAppToLinkedRepositoriesMap(apps));
        }
    }, [apps, version]);

    const refreshApps = useCallback(() => {
        useCoreStore.getState().refetchApplications?.();
        useCoreStore.getState().refetchHosts?.();
    }, []);

    const refetchSummary = useCallback(() => {
        useCoreStore.getState().refetchAssessmentsSummary?.();
    }, []);

    const contextValue = useMemo(() => ({
        appComponents,
        appInstances,
        appLinkedRepositoriesMap,
        apps,
        appTable,
        assessmentsById,
        clientAssessment,
        enabled,
        id,
        isSeparateRouteAssessmentsDetails,
        refetchSummary,
        refreshApps,
        setAppTable,
        setEnabled,
        version
    }), [
        appComponents,
        appInstances,
        appLinkedRepositoriesMap,
        apps,
        appTable,
        assessmentsById,
        clientAssessment,
        enabled,
        id,
        isSeparateRouteAssessmentsDetails,
        refetchSummary,
        refreshApps,
        version
    ]);

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
}

AppContextProvider.propTypes = propTypes;

export { AppContext, AppContextProvider };
